import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import InfiniteScroll from "react-infinite-scroll-component";
import { MdDownload } from "react-icons/md";
import * as Common from "../../../Components/Common";
import { STATUS_CONFIG, TOAST_MESSAGES } from "../constants";
import {
  getSubmilestones,
  uploadMilestoneFile,
  getFiles,
  updateSubMilestoneStatus,
  deleteFile,
  updateMilestoneStatus,
} from "../../../Services/projectServices";
import { ROLES } from "../../../Constants/Strings";
import { useNotifications } from "../../../Context/NotificationContext";
import { FiTrash2 } from "react-icons/fi";
import { downloadUtil } from "../../../Utils/DownloadUtil";

const RESET_PAGINATION = { offset: 0, limit: 3, count: 0 };

const SubMilestones = ({
  selectedMilestone,
  projectId,
  setIsSubMilestoneOpen,
  fetchMilestoneData,
  setSelectedMilestone,
  setProjects,
}) => {
  const [submilestones, setSubmilestones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileUploadLoading, setUploadLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedSubMilestone, setSelectedSubMilestone] = useState(null);
  const [filePagination, setFilePagination] = useState(RESET_PAGINATION);
  const [fileLoadingMore, setFileLoadingMore] = useState(false);
  const { fetchUnreadCount } = useNotifications();

  const handleFileChange = async (file) => {
    if (!file) return;
    setUploadLoading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("milestone", selectedMilestone.id);

    if (selectedSubMilestone) {
      formData.append("sub_milestone", selectedSubMilestone.id);
    }

    try {
      const response = await uploadMilestoneFile(projectId, formData);
      if (response.status === 201) {
        toast.success(TOAST_MESSAGES.FILE_UPLOADED_SUCCESFULLY);
        fetchFilesData(true);
      } else {
        toast.error(TOAST_MESSAGES.FILE_UPLOAD_FAILED);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          TOAST_MESSAGES.FILE_UPLOAD_FAILED
      );
    } finally {
      setUploadLoading(false);
    }
  };

  const getStatusProps = (status) => {
    if (!status) return STATUS_CONFIG.default;
    const key = status.toLowerCase();
    return STATUS_CONFIG[key] || STATUS_CONFIG.default;
  };

  const fetchData = () => {
    getSubmilestones(projectId, selectedMilestone.id)
      .then((response) => {
        if (response.status === 200) {
          setSubmilestones(response?.data?.results || []);
          setSelectedSubMilestone(response?.data?.results[0] || null);
        } else {
          toast.error(TOAST_MESSAGES.SUBMILESTONE_FETCH_FAIL);
        }
      })
      .catch(() => {
        toast.error(TOAST_MESSAGES.SUBMILESTONE_FETCH_FAIL);
      });
  };

  // const updateCurrentMilestoneStatus = async(status) => {
  //   if (!selectedSubMilestone) {
  //     toast.error("No submilestone selected");
  //     return;
  //   }
  //   await updateSubMilestoneStatus(
  //     projectId,
  //     selectedMilestone.id,
  //     selectedSubMilestone.id,
  //     { status }
  //   )
  //     .then((response) => {
  //       if (response.status === 200) {
  //         setSubmilestones((prev) =>
  //           prev.map((sm) =>
  //             sm.id === selectedSubMilestone.id ? { ...sm, status } : sm
  //           )
  //         );
  //         setSelectedSubMilestone((prev) =>
  //           prev ? { ...prev, status } : prev
  //         );
  //         fetchUnreadCount();
  //         fetchMilestoneData();
  //         fetchData();
  //       } else {
  //         toast.error("Failed to update the status");
  //       }
  //     })
  //     .catch((error) => {
  //       console.error("Update error:", error);
  //       toast.error("Failed to update the status");
  //     });
  // };

  const updateCurrentMilestoneStatus = async (status) => {
  if (!selectedSubMilestone) {
    toast.error("No submilestone selected");
    return;
  }
  try {
    const response = await updateSubMilestoneStatus(
      projectId,
      selectedMilestone.id,
      selectedSubMilestone.id,
      { status }
    );
    if (response.status === 200) {
      // ✅ Update submilestones locally
      setSubmilestones((prev) =>
        prev.map((sm) =>
          sm.id === selectedSubMilestone.id ? { ...sm, status } : sm
        )
      );
      setSelectedSubMilestone((prev) => prev ? { ...prev, status } : prev);

      // ✅ If status is IN_PROGRESS, update milestone immediately
      if (status === "IN_PROGRESS" && selectedMilestone.status === "PENDING") {
        setSelectedMilestone((prev) => ({ ...prev, status: "IN_PROGRESS" }));
      }

      fetchUnreadCount();
      fetchMilestoneData();   // still call backend for sync
    } else {
      toast.error( response.error?.errors?.error[0] ||"Failed to update the status");
    }
  } catch (error) {
    toast.error("Failed to update the status");
  }
};


  const fetchFilesData = (reset = false) => {
    const isInitial = reset || filePagination.offset === 0;
    if (isInitial) setLoading(true);
    else setFileLoadingMore(true);

    const params = {
      milestone: selectedMilestone.id,
      offset: isInitial ? 0 : filePagination.offset,
      limit: filePagination.limit,
    };

    if (selectedSubMilestone) {
      params.sub_milestone = selectedSubMilestone.id;
    }

    getFiles(projectId, params)
      .then((response) => {
        if (response.status === 200) {
          const newFiles = response.data.results || [];
          setFiles((prev) => (isInitial ? newFiles : [...prev, ...newFiles]));
          setFilePagination((prev) => ({
            ...prev,
            count: response.data.count,
            offset: (isInitial ? 0 : prev.offset) + prev.limit,
          }));
        } else {
          toast.error(TOAST_MESSAGES.SUBMILESTONE_FETCH_FAIL);
        }
      })
      .catch(() => {
        toast.error(TOAST_MESSAGES.SUBMILESTONE_FETCH_FAIL);
      })
      .finally(() => {
        setLoading(false);
        setFileLoadingMore(false);
      });
  };

  const loadMoreFiles = () => {
    if (filePagination.offset >= filePagination.count) return;
    fetchFilesData();
  };

  const handleDownload = async (fileUrl) => {
    try {
      await downloadUtil(fileUrl);
      toast.success(TOAST_MESSAGES.FILE_DOWNLOAD_SUCCESSFUL);
    } catch {
      toast.error(TOAST_MESSAGES.FILE_DOWNLOAD_FAIL);
    }
  };

  const handleDelete = async (fileId) => {
    try {
      setLoading(true);
      const response = await deleteFile(projectId, fileId);
      if (response.status === 204 || response.status === 200) {
        toast.success(TOAST_MESSAGES.FILE_DELETE_SUCCESS);
        setFilePagination(RESET_PAGINATION);
        fetchFilesData(true);
      } else {
        toast.error(TOAST_MESSAGES.FILE_DELETE_FAIL);
      }
    } catch {
      toast.error(TOAST_MESSAGES.ERROR_DELETE_FILE);
    } finally {
      setLoading(false);
    }
  };

  const handleRenderField = (key, rowData) => {
    if (key === "actions") {
      return (
        <div key={rowData.id} className="flex items-center gap-6">
          <button onClick={() => handleDownload(rowData.file)} title="Download">
            <MdDownload
              size={20}
              className="text-gray-500 hover:text-blue-600"
            />
          </button>
          <button
            disabled={isDisabled}
            className={`p-2 rounded ${
              isDisabled
                ? "text-gray-400 cursor-not-allowed"
                : "text-red-500 hover:text-blue-600"
            }`}
            onClick={() => handleDelete(rowData.id)}
            title={`${
              subStatus === "IN_PROGRESS" && milestoneStatus === "IN_PROGRESS"
                ? "Delete File"
                : subStatus === "COMPLETED" || milestoneStatus === "COMPLETED"
                ? "Can not delete, already completed"
                : ""
            }`}
          >
            <FiTrash2 size={20} />
          </button>
        </div>
      );
    }
    if (key === "file") {
      const fileName = rowData.file?.split("/").pop().split("?")[0];
      return (
        <a
          href={rowData.file}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
          title={fileName}
        >
          {fileName || "N/A"}
        </a>
      );
    }
    return rowData[key] || "N/A";
  };

  // ✅ Safe checks for statuses
  const milestoneStatus = selectedMilestone?.status || "";
  const subStatus = selectedSubMilestone?.status || "";

  // ✅ Safe calculation of disabled state
  const isDisabled =
    milestoneStatus === "PENDING" ||
    milestoneStatus === "COMPLETED" ||
    (selectedSubMilestone &&
      (subStatus === "PENDING" || subStatus === "COMPLETED"));

  useEffect(() => {
    if (selectedMilestone) {
      fetchData();
    }
  }, [selectedMilestone]);

  useEffect(() => {
    setFilePagination(RESET_PAGINATION);
    if (selectedMilestone) {
      fetchFilesData(true);
    }
    // ✅ fetch files even if no submilestone (top-level files)
  }, [selectedSubMilestone, selectedMilestone]);

  const handleMilestoneStatusUpdate = async (status) => {
    if (!selectedMilestone) {
      toast.error("No milestone selected");
      return;
    }
    try {
      const response = await updateMilestoneStatus(
        projectId,
        selectedMilestone.id,
        { status }
      );
      if (response.status === 200) {
        setSelectedMilestone(response.data);
        setSelectedSubMilestone((prev) => ({
          ...(prev || {}),
          status: status === "IN_PROGRESS" ? "IN_PROGRESS" : prev?.status,
        }));
        setProjects((prevProjects) =>
          prevProjects.map((project) =>
            project.id === response.data.project
              ? {
                  ...project,
                  milestones: project.milestones.map((m) =>
                    m.id === response.data.id ? response.data : m
                  ),
                }
              : project
          )
        );
        fetchData();
      } else {
        toast.error("Failed to update milestone status");
      }
    } catch {
      toast.error("Failed to update milestone status");
    }
  };

  const showMilestoneButton = [
    "Sanction Letter",
    "Structure",
    "Commission",
  ].includes(selectedMilestone?.title);


  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={() => setIsSubMilestoneOpen(false)}
    >
      <div
        className="max-w-[85vw] min-w-[40vw] bg-white rounded-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-center font-bold pt-2 px-8 text-2xl">
            <span>{selectedMilestone?.title}</span>
           { showMilestoneButton && (<button
              className="flex items-center text-red-500 rounded-m"
              onClick={() => setIsSubMilestoneOpen(false)}
              title="Close"
            >
              X
            </button>)}
          </div>

          <div className="border-b-2"></div>
        </div>

        {/* ✅ Render only if submilestones exist */}
        {submilestones.length > 0 && (
          <div className="relative w-full">
            <div className="flex justify-evenly items-start relative w-full py-2 px-3 overflow-x-auto">
              {submilestones.map((milestone, index, arr) => {
                const { color, bg, icon, text } = getStatusProps(
                  milestone.status
                );
                return (
                  <div
                    key={milestone.id}
                    onClick={() => setSelectedSubMilestone(milestone)}
                    className={`flex flex-col items-center text-center relative z-10 min-w-[6rem] cursor-pointer p-4 rounded-md font-medium ${
                      selectedSubMilestone?.id === milestone.id
                        ? "bg-gray-200"
                        : ""
                    }`}
                    style={{ flex: 1 }}
                  >
                    {index > 0 && (
                      <div
                        className={`absolute top-8 left-0 w-1/2 h-1 -z-10 ${
                          getStatusProps(arr[index - 1].status).color
                        }`}
                      />
                    )}
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${color}`}
                    >
                      {icon}
                    </div>
                    {index < arr.length - 1 && (
                      <div
                        className={`absolute top-8 right-0 w-1/2 h-1 ${color} -z-10`}
                      />
                    )}
                    <div className="mt-2 text-xs whitespace-nowrap">
                      {milestone.title}
                    </div>
                    <div
                      className={`mt-1 text-[10px] px-2 py-0.5 rounded-full ${bg}`}
                    >
                      {text}
                    </div>
                  </div>
                );
              })}
            </div>

            <Common.RoleValidator allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
              <div className="flex justify-end px-4">
                {subStatus === "PENDING" && (
                  <button
                    className="py-1 px-2 bg-blue-300 text-gray-900 m-2 rounded-lg text-[11px] md:text-sm"
                    onClick={() => updateCurrentMilestoneStatus("IN_PROGRESS")}
                  >
                    Mark as In Progress
                  </button>
                )}
                {subStatus === "IN_PROGRESS" && (
                  <button
                    className="py-1 px-2 bg-green-300 text-gray-900 m-2 rounded-lg text-[11px] md:text-sm"
                    onClick={() => updateCurrentMilestoneStatus("COMPLETED")}
                  >
                    Mark as Completed
                  </button>
                )}
                {subStatus === "COMPLETED" && (
                  <span className="py-1 px-2 bg-gray-300 text-gray-700 m-2 rounded-lg text-[11px] md:text-sm">
                    Already Completed
                  </span>
                )}
              </div>
            </Common.RoleValidator>
            <hr className="border-t border-gray-300" />
          </div>
        )}

        {/* File uploader & list */}
        <div>
          <div
            id="fileScrollDiv"
            className="h-44 overflow-x-hidden overflow-y-scroll thin-scrollbar p-4"
          >
            {loading ? (
              <div className="flex justify-center items-center">
                <Common.Spinner />
              </div>
            ) : (
              <InfiniteScroll
                dataLength={files.length}
                next={loadMoreFiles}
                hasMore={filePagination.offset < filePagination.count}
                scrollableTarget="fileScrollDiv"
                loader={
                  fileLoadingMore && (
                    <div className="flex justify-center py-2">
                      <Common.Spinner size={3} />
                    </div>
                  )
                }
              >
                <Common.CommonTable
                  tableColumns={["file", "actions"]}
                  columnLabels={{ file: "File", actions: "Actions" }}
                  tableRows={files}
                  renderField={handleRenderField}
                  className="border-2 shadow-md text-md"
                />
              </InfiniteScroll>
            )}
          </div>

          <div className="flex flex-col">
            <div className="border-t-2"></div>
          </div>
          <div className="flex justify-between mt-2 gap-2 items-center p-4">
            <Common.RoleValidator allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
              <div
                className="py-1"
                title={`${
                  subStatus === "PENDING"
                    ? "Sub-milestone is pending"
                    : subStatus === "IN_PROGRESS"
                    ? "Upload File"
                    : subStatus === "COMPLETED"
                    ? "Sub-milestone completed"
                    : ""
                }`}
              >
                <Common.FileUploader
                  onUploadClick={handleFileChange}
                  isUploading={fileUploadLoading}
                  singleButton={true}
                  disabled={isDisabled}
                  singleBtnClassName={`px-2 py-1 border rounded-md font-medium ${
                    isDisabled
                      ? "text-gray-700 bg-opacity-50 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-700 text-white"
                  }`}
                />
              </div>
            </Common.RoleValidator>

            <Common.RoleValidator allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
              {showMilestoneButton && (
                <div className="flex justify-end">
                  {milestoneStatus === "PENDING" && (
                    <button
                      className="px-2 py-1 border font-medium bg-blue-300 text-gray-900  m-2 rounded-md"
                      onClick={() => handleMilestoneStatusUpdate("IN_PROGRESS")}
                    >
                      Mark as In Progress
                    </button>
                  )}
                  {milestoneStatus === "IN_PROGRESS" && (
                    <button
                      className="px-2 py-1 border font-medium bg-green-300 text-gray-900  m-2 rounded-md"
                      onClick={() => handleMilestoneStatusUpdate("COMPLETED")}
                    >
                      Mark as Completed
                    </button>
                  )}
                  {milestoneStatus === "COMPLETED" && (
                    <span className="px-2 py-1 border font-medium bg-gray-300 text-gray-700  m-2 rounded-md">
                      Already Completed
                    </span>
                  )}
                </div>
              )}
            </Common.RoleValidator>

           { !showMilestoneButton && (<button
              className="flex items-center px-2 py-1 font-medium bg-blue-500 text-white rounded-md hover:bg-blue-700"
              onClick={() => setIsSubMilestoneOpen(false)}
            >
              Cancel
            </button>)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubMilestones;
